import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { CheckCircle2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SolutionChatScenario } from '@/types/scenarios/solution';

interface SolutionRealWorldScenariosProps {
  title?: string;
  subtitle?: string;
  scenarios: SolutionChatScenario[];
}

const SCENARIO_DURATION = 15000;
const INITIAL_DELAY = 3000;

export const SolutionRealWorldScenarios = ({
  title = "Never miss another opportunity",
  subtitle = "See how your Genie handles real conversations — capturing leads, answering questions, and keeping customers happy.",
  scenarios,
}: SolutionRealWorldScenariosProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  const [chatFaded, setChatFaded] = useState(false);
  const [timeIntoScenario, setTimeIntoScenario] = useState(0);
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [scenarioStarted, setScenarioStarted] = useState(false);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  const FULL_LOOP_DURATION = INITIAL_DELAY + (scenarios.length * SCENARIO_DURATION);

  // Animation loop using requestAnimationFrame
  useEffect(() => {
    if (!isInView) {
      setScenarioStarted(false);
      setScenarioIndex(0);
      setTimeIntoScenario(0);
      return;
    }

    startTimeRef.current = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTimeRef.current;
      const loopTime = elapsed % FULL_LOOP_DURATION;

      if (loopTime < INITIAL_DELAY) {
        setScenarioStarted(false);
        setScenarioIndex(0);
        setTimeIntoScenario(0);
        setChatFaded(false);
      } else {
        const timeIntoScenarios = loopTime - INITIAL_DELAY;
        const newScenarioIndex = Math.min(
          Math.floor(timeIntoScenarios / SCENARIO_DURATION),
          scenarios.length - 1
        );
        const timeIntoCurrentScenario = timeIntoScenarios % SCENARIO_DURATION;

        setScenarioStarted(true);
        setScenarioIndex(newScenarioIndex);
        setTimeIntoScenario(timeIntoCurrentScenario);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isInView, scenarios.length, FULL_LOOP_DURATION]);

  const handleReplay = () => {
    startTimeRef.current = performance.now();
    setScenarioIndex(0);
    setTimeIntoScenario(0);
    setScenarioStarted(false);
    setChatFaded(false);
  };

  const handleCardsStart = useCallback(() => {
    setChatFaded(true);
  }, []);

  // Handle scroll visibility
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (windowHeight - rect.top) / (windowHeight * 0.8)));

      const nowInView = progress > 0.3;
      if (nowInView && !isInView) {
        setIsInView(true);
        setChatFaded(false);
      } else if (!nowInView && isInView) {
        setIsInView(false);
        setChatFaded(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInView]);

  const currentScenario = scenarios[scenarioIndex];
  const outcomes = currentScenario?.outcomes || [];

  const visibleMessages = useMemo(() => {
    if (!currentScenario) return [];
    return currentScenario.messages.filter(msg => timeIntoScenario >= msg.delay);
  }, [currentScenario, timeIntoScenario]);

  const isTyping = useMemo(() => {
    if (!currentScenario) return false;
    return currentScenario.messages.some(msg =>
      msg.type === 'genie' &&
      timeIntoScenario >= msg.delay - 1000 &&
      timeIntoScenario < msg.delay
    );
  }, [currentScenario, timeIntoScenario]);

  const visibleCardIds = useMemo(() => {
    return outcomes
      .filter(card => timeIntoScenario >= card.delay)
      .map(card => card.id);
  }, [outcomes, timeIntoScenario]);

  // Notify when cards start appearing
  useEffect(() => {
    if (visibleCardIds.length > 0 && !chatFaded) {
      handleCardsStart();
    }
  }, [visibleCardIds.length, chatFaded, handleCardsStart]);

  if (!scenarios || scenarios.length === 0) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden pt-16 lg:pt-24 pb-16 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-5 leading-tight tracking-tight">
            {title.split('\n').map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-500 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Three column layout: Tabs | Chat | Outcomes */}
        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_280px] gap-8 lg:gap-10 items-start">
          {/* Left column - Scenario tabs */}
          <div className="flex flex-col gap-2">
            {scenarios.map((scenario, index) => (
              <button
                key={scenario.id}
                onClick={() => {
                  const targetTime = INITIAL_DELAY + index * SCENARIO_DURATION;
                  startTimeRef.current = performance.now() - targetTime;
                }}
                className={cn(
                  'px-4 py-3 rounded-lg text-sm font-medium transition-all text-left',
                  scenarioIndex === index
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                )}
              >
                {scenario.title}
              </button>
            ))}

            <button
              onClick={handleReplay}
              className="mt-2 flex items-center gap-1.5 text-gray-400 hover:text-gray-600 transition-colors text-xs"
              aria-label="Replay scenario"
            >
              <RotateCcw size={14} />
              <span>Replay</span>
            </button>
          </div>

          {/* Center column - Chat bubbles */}
          <div
            className={cn(
              'min-h-[480px] flex flex-col justify-start transition-opacity duration-300',
              chatFaded ? 'opacity-60' : 'opacity-100'
            )}
            style={{ opacity: isInView ? 1 : 0.5 }}
          >
            {isInView && scenarioStarted && currentScenario && (
              <div className="flex flex-col gap-3">
                {visibleMessages.map((message, index) => (
                  <div
                    key={`${currentScenario.id}-${index}`}
                    className={cn(
                      'flex items-end gap-2',
                      message.type === 'user' ? 'justify-end' : 'justify-start',
                      'animate-fade-slide-in'
                    )}
                  >
                    {message.type === 'genie' && (
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                        <img
                          src="/genie-mascots/role/support.png"
                          alt="Genie"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div
                      className={cn(
                        'px-4 py-3 rounded-2xl shadow-lg backdrop-blur-sm',
                        'text-sm lg:text-base leading-relaxed',
                        message.type === 'user'
                          ? 'bg-white text-gray-800 rounded-br-sm border border-gray-100 max-w-[85%]'
                          : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-bl-sm max-w-[calc(85%-40px)]'
                      )}
                    >
                      {message.text}
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex items-end gap-2 justify-start animate-fade-slide-in">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white shadow-md overflow-hidden">
                      <img
                        src="/genie-mascots/role/support.png"
                        alt="Genie"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gradient-to-r from-purple-600 to-blue-600 shadow-lg">
                      <div className="flex gap-1.5">
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                        <span className="w-2 h-2 bg-white/70 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {(!isInView || !scenarioStarted) && (
              <div className="flex flex-col gap-3 opacity-30">
                <div className="flex justify-end">
                  <div className="px-4 py-3 rounded-2xl rounded-br-sm bg-gray-100 text-gray-400 max-w-[80%]">
                    Customer message appears here...
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-gray-200 text-gray-400 max-w-[80%]">
                    Genie response appears here...
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column - Outcome checklist */}
          <div style={{ opacity: isInView ? 1 : 0.5 }}>
            {isInView && scenarioStarted && (
              <div className="flex flex-col gap-2.5">
                {outcomes.map((outcome) => {
                  const isVisible = visibleCardIds.includes(outcome.id);

                  return (
                    <div
                      key={`${currentScenario?.id}-${outcome.id}`}
                      className={cn(
                        'flex items-center gap-3 px-4 py-3 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-400 ease-out',
                        isVisible
                          ? 'opacity-100 translate-x-0'
                          : 'opacity-0 translate-x-4 pointer-events-none'
                      )}
                    >
                      <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{outcome.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {(!isInView || !scenarioStarted) && (
              <div className="flex flex-col gap-2.5 opacity-30">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-100">
                    <div className="w-5 h-5 rounded-full bg-gray-200" />
                    <div className="h-4 w-40 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-slide-in {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-slide-in {
          animation: fade-slide-in 0.25s ease-out forwards;
        }
      `}</style>
    </section>
  );
};
